import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BeakerIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  UserPlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  LockClosedIcon,
  Bars3Icon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';

// Helpers para renderização segura
const asText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
};

const shortText = (v, n = 30) => {
  const s = asText(v);
  return s.length > n ? s.slice(0, n) + '...' : s;
};

// ✅ Normalizador de headers para o schema esperado
const normalizeHeadersToSchema = (headers) => {
  // Se já vier no formato { type: 'object', properties: { ... } }, só garante que properties existe
  if (
    headers &&
    typeof headers === 'object' &&
    headers.type === 'object' &&
    headers.properties
  ) {
    return {
      type: 'object',
      properties: { ...headers.properties },
    };
  }

  const schema = {
    type: 'object',
    properties: {},
  };

  if (!headers || typeof headers !== 'object') return schema;

  Object.entries(headers).forEach(([key, value]) => {
    if (value === undefined) return;

    const field = {};

    if (Array.isArray(value)) {
      field.type = 'array';
      field.value = value;
    } else if (value !== null && typeof value === 'object') {
      field.type = 'object';
      field.value = value;
    } else if (typeof value === 'number') {
      field.type = Number.isInteger(value) ? 'integer' : 'number';
      field.value = value;
    } else if (typeof value === 'boolean') {
      field.type = 'boolean';
      field.value = value;
    } else if (value === null) {
      field.type = 'null';
      field.value = null;
    } else {
      field.type = 'string';
      field.value = String(value);
    }

    schema.properties[key] = field;
  });

  return schema;
};

// Converte schema do corpo (backend) para lista de propriedades (builder visual) – suporta object aninhados
const bodySchemaToProperties = (body) => {
  if (!body || typeof body !== 'object' || !body.properties || typeof body.properties !== 'object') {
    return [];
  }
  const required = Array.isArray(body.required) ? body.required : [];
  return Object.entries(body.properties).map(([name, prop]) => {
    const p = typeof prop === 'object' && prop !== null ? prop : { type: 'string' };
    let defaultValue = '';
    if (p.default !== undefined) defaultValue = String(p.default);
    if (p.value !== undefined) defaultValue = String(p.value);
    const item = {
      name,
      type: p.type || 'string',
      defaultValue,
      description: p.description || '',
      enumValues: Array.isArray(p.enum) ? [...p.enum] : [],
      required: required.includes(name),
    };
    if (item.type === 'object' && p.properties && typeof p.properties === 'object') {
      item.children = bodySchemaToProperties({
        type: 'object',
        properties: p.properties,
        required: Array.isArray(p.required) ? p.required : [],
      });
    }
    return item;
  });
};

// Converte lista de propriedades (builder) para schema do corpo (backend) – suporta children em object
const bodyPropertiesToSchema = (properties) => {
  const props = Array.isArray(properties) ? properties : [];
  const schema = { type: 'object', properties: {}, required: [] };
  props.forEach((p) => {
    if (!p || !String(p.name).trim()) return;
    const key = String(p.name).trim();
    let field;
    if (p.type === 'object') {
      const nested = bodyPropertiesToSchema(Array.isArray(p.children) ? p.children : []);
      field = { type: 'object', properties: nested.properties, required: nested.required };
      if ((p.description || '').trim()) field.description = (p.description || '').trim();
    } else {
      field = { type: p.type || 'string', description: (p.description || '').trim() || undefined };
      if (field.description === undefined) delete field.description;
      if (p.defaultValue !== undefined && String(p.defaultValue).trim() !== '') {
        const v = p.defaultValue.trim();
        if (p.type === 'number' || p.type === 'integer') field.default = Number(v);
        else if (p.type === 'boolean') field.default = v === 'true';
        else field.default = v;
      }
      if (Array.isArray(p.enumValues) && p.enumValues.length > 0) {
        field.enum = p.enumValues.map((e) => String(e).trim()).filter(Boolean);
      }
    }
    schema.properties[key] = field;
    if (p.required) schema.required.push(key);
  });
  return schema;
};

// Helpers para árvore de propriedades: path = array de índices (ex: [] = root, [0] = children do 1º item)
const getPropertiesAtPath = (root, path) => {
  if (!path || path.length === 0) return root;
  let arr = root;
  for (let i = 0; i < path.length; i++) {
    const idx = path[i];
    if (!arr || !arr[idx]) return [];
    arr = arr[idx].children || [];
  }
  return arr;
};

const setPropertiesAtPath = (root, path, newChildren) => {
  if (!path || path.length === 0) return newChildren;
  const [first, ...rest] = path;
  return root.map((item, i) =>
    i === first
      ? { ...item, children: setPropertiesAtPath(item.children || [], rest, newChildren) }
      : item
  );
};

const removePropertyAtPath = (root, path) => {
  if (!path || path.length === 0) return root;
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  const parentArr = getPropertiesAtPath(root, parentPath);
  const newParent = parentArr.filter((_, j) => j !== index);
  return setPropertiesAtPath(root, parentPath, newParent);
};

// Item do builder de corpo (uma propriedade; se type object, expansível com filhos)
const BodyPropertyItem = ({
  prop,
  index,
  path,
  pathKey,
  bodyProperties,
  setBodyProperties,
  expandedBodyPaths,
  setExpandedBodyPaths,
  setEditingPropertyIndex,
  setEditingPropertyPath,
  setEditingProperty,
  getPropertiesAtPath,
  setPropertiesAtPath,
  labelStyle,
  inputStyle,
}) => {
  const isObject = prop.type === 'object';
  const expanded = expandedBodyPaths.has(pathKey);
  const children = prop.children || [];
  const toggleExpanded = () => {
    setExpandedBodyPaths((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  };

  return (
    <li className="rounded-lg border border-border overflow-hidden bg-muted/20">
      <div className="flex items-center justify-between p-3 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isObject && (
            <button
              type="button"
              onClick={toggleExpanded}
              className="shrink-0 p-0.5 rounded hover:bg-muted text-muted-foreground"
              aria-label={expanded ? 'Recolher' : 'Expandir'}
            >
              {expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
            </button>
          )}
          <span className="font-medium text-foreground truncate">{prop.name || '(sem nome)'}</span>
          {prop.required && <span className="text-destructive">*</span>}
          <Badge variant="secondary" className="text-xs shrink-0">{prop.type}</Badge>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              setEditingPropertyPath(path.length > 1 ? path.slice(0, -1) : []);
              setEditingPropertyIndex(index);
              setEditingProperty({ ...prop, enumValues: prop.enumValues || [], children: prop.children });
            }}
            title="Editar"
          >
            <PencilSquareIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setBodyProperties(removePropertyAtPath(bodyProperties, path))}
            title="Remover"
          >
            <TrashIcon className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      {isObject && expanded && (
        <div className="border-t border-border bg-muted/10 pl-4 pr-4 pb-4 pt-3 space-y-3">
          {prop.description && (
            <p className="text-sm text-muted-foreground"><span className="font-medium">Descrição:</span> {prop.description}</p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-green-600/10 border-green-600/30 text-green-700 dark:text-green-400 hover:bg-green-600/20"
            onClick={() => {
              setEditingPropertyPath(path);
              setEditingPropertyIndex(-1);
              setEditingProperty({ name: '', type: 'string', defaultValue: '', description: '', enumValues: [], required: false });
            }}
          >
            <PlusCircleIcon className="h-4 w-4 mr-1" />
            Adicionar propriedade
          </Button>
          {children.length === 0 ? (
            <div className="py-6 text-center border border-dashed border-border rounded-lg">
              <p className="text-sm text-muted-foreground">Nenhum parâmetro definido.</p>
              <p className="text-xs text-muted-foreground mt-1">Clique em &quot;Adicionar propriedade&quot; para definir os parâmetros.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {children.map((child, j) => (
                <BodyPropertyItem
                  key={j}
                  prop={child}
                  index={j}
                  path={[...path, j]}
                  pathKey={`${pathKey}-${j}`}
                  bodyProperties={bodyProperties}
                  setBodyProperties={setBodyProperties}
                  expandedBodyPaths={expandedBodyPaths}
                  setExpandedBodyPaths={setExpandedBodyPaths}
                  setEditingPropertyIndex={setEditingPropertyIndex}
                  setEditingPropertyPath={setEditingPropertyPath}
                  setEditingProperty={setEditingProperty}
                  getPropertiesAtPath={getPropertiesAtPath}
                  setPropertiesAtPath={setPropertiesAtPath}
                  labelStyle={labelStyle}
                  inputStyle={inputStyle}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

// Converte headers do backend (schema ou objeto chave-valor) para lista chave-valor
const headersSchemaToList = (headers) => {
  if (!headers || typeof headers !== 'object') return [];
  // Formato schema: { type: 'object', properties: { Nome: { type: 'string', value: '...' } } }
  if (headers.type === 'object' && headers.properties && typeof headers.properties === 'object') {
    return Object.entries(headers.properties).map(([key, val]) => ({
      key,
      value: val && typeof val === 'object' && val.value !== undefined ? String(val.value) : '',
    }));
  }
  // Formato objeto simples (evita ignorar headers vindos do backend em outro formato)
  return Object.entries(headers)
    .filter(([k]) => k !== 'type' && k !== 'properties' && k !== 'required')
    .map(([key, val]) => ({
      key,
      value: typeof val === 'object' && val !== null && 'value' in val ? String(val.value ?? '') : String(val ?? ''),
    }));
};

// Converte lista chave-valor para schema de headers (backend)
const headersListToSchema = (list) => {
  const arr = Array.isArray(list) ? list : [];
  const properties = {};
  arr.forEach(({ key, value }) => {
    const k = String(key || '').trim();
    if (!k) return;
    properties[k] = { type: 'string', value: String(value ?? '') };
  });
  return { type: 'object', properties };
};

// Estilos
const labelStyle = "block text-sm font-medium text-foreground mb-1";
const inputStyle = "w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const selectStyle = "w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

// Modal
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <Card className="relative w-full max-w-2xl">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
        <CardContent className="pt-6">{children}</CardContent>
      </Card>
    </div>
  );
};

// Seção colapsável (estilo provedor)
const CollapsibleSection = ({ title, description, icon: Icon, open, onToggle, badgeCount, children }) => (
  <div className="border border-border rounded-lg overflow-hidden bg-card">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
        <span className="font-medium text-foreground">{title}</span>
        {badgeCount !== undefined && badgeCount > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-xs text-white">
            {badgeCount}
          </span>
        )}
      </div>
      {open ? <ChevronUpIcon className="h-5 w-5 text-muted-foreground" /> : <ChevronDownIcon className="h-5 w-5 text-muted-foreground" />}
    </button>
    {open && (
      <div className="px-4 pb-4 border-t border-border">
        {description && <p className="text-sm text-muted-foreground mt-3 mb-3">{description}</p>}
        {children}
      </div>
    )}
  </div>
);

// Form
const ToolForm = ({ tool, onSave, onCancel, isSubmitting, clienteId }) => {
  // Buscar orgID do localStorage para preencher automaticamente
  const orgIDFromStorage = localStorage.getItem('orgID') || '';
  // Estado do builder visual (apiRequest): seções, cabeçalhos e propriedades do corpo
  const [apiRequestSections, setApiRequestSections] = useState({ base: true, auth: false, headers: true, body: true });
  const [headersList, setHeadersList] = useState([]);
  const [bodyProperties, setBodyProperties] = useState([]);
  const [editingPropertyIndex, setEditingPropertyIndex] = useState(null);
  const [editingPropertyPath, setEditingPropertyPath] = useState([]); // path onde está editando ([] = root)
  const [editingProperty, setEditingProperty] = useState(null);
  const [expandedBodyPaths, setExpandedBodyPaths] = useState(() => new Set(['0'])); // paths expandidos (ex: '0', '0-1')

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'apiRequest',
    url: '',
    method: 'POST',
    bodySchema: JSON.stringify({
      type: "object",
      properties: {
        query: { type: "string", description: "A query de busca." }
      }
    }, null, 2),
    showHeaders: false,
    headers: JSON.stringify({
      type: "object",
      properties: {
        apikey: {
          type: "string",
          value: "discadornet"
        }
      }
    }, null, 2),
    destinationNumber: '',
    transferMessage: '',
    transferTimeout: '60',
    holdAudioUrl: '',
    numberE164CheckEnabled: true,
    orgId: tool?.orgId || orgIDFromStorage, // Preencher com orgID do localStorage se não houver tool sendo editada
    metadata: JSON.stringify({
      "calendarId": "exemplo@gmail.com",
      "timeZone": "America/Sao_Paulo"
    }, null, 2),
    messages: JSON.stringify([{
      "type": "request-start",
      "blocking": false
    }], null, 2),
    parameters: JSON.stringify({
      "type": "object",
      "properties": {},
      "required": []
    }, null, 2),
    voicemailOption: 'assistant', // 'assistant', 'custom', 'avoid'
    voicemailCustomScript: '',
    beepDetectionEnabled: false,
    ...tool
  });

  useEffect(() => {
    if (tool) {
      setFormData(prev => {
        const newData = { ...prev, ...tool };
        // Se a tool não tiver orgId, usar o do localStorage
        if (!newData.orgId && orgIDFromStorage) {
          newData.orgId = orgIDFromStorage;
        }

        // Transfer Call
        if (tool.type === 'transferCall' && tool.destinations && tool.destinations.length > 0) {
          const dest = tool.destinations[0];
          newData.destinationNumber = dest.number || '';
          newData.transferMessage = dest.message || '';
          newData.transferTimeout = dest.transferPlan?.timeout || '60';
          newData.holdAudioUrl = dest.transferPlan?.holdAudioUrl || '';
          newData.numberE164CheckEnabled = dest.numberE164CheckEnabled !== false;
        }

        // API Request body (pode vir objeto ou string do backend)
        if (tool.type === 'apiRequest') {
          const bodyRaw = tool.body;
          const bodyObj = bodyRaw == null ? {} : (typeof bodyRaw === 'string' ? (() => { try { return JSON.parse(bodyRaw); } catch { return {}; } })() : bodyRaw);
          if (Object.keys(bodyObj).length > 0 || bodyObj.properties) {
            newData.bodySchema = JSON.stringify(bodyObj, null, 2);
          }
        }

        // API Request headers (pode vir objeto ou string do backend)
        if (tool.type === 'apiRequest') {
          const headersRaw = tool.headers;
          const headersObj = headersRaw == null ? {} : (typeof headersRaw === 'string' ? (() => { try { return JSON.parse(headersRaw); } catch { return {}; } })() : headersRaw);
          if (headersObj && typeof headersObj === 'object') {
            if (headersObj.type === 'object' && headersObj.properties) {
              newData.headers = JSON.stringify(headersObj, null, 2);
              newData.showHeaders = Object.keys(headersObj.properties || {}).length > 0;
            } else if (Object.keys(headersObj).filter((k) => !['type', 'properties', 'required'].includes(k)).length > 0) {
              const normalized = { type: 'object', properties: {} };
              Object.entries(headersObj).forEach(([key, value]) => {
                if (['type', 'properties', 'required'].includes(key)) return;
                normalized.properties[key] = typeof value === 'object' && value !== null && 'value' in value
                  ? value
                  : { type: 'string', value: value != null ? String(value) : '' };
              });
              newData.headers = JSON.stringify(normalized, null, 2);
              newData.showHeaders = Object.keys(normalized.properties).length > 0;
            }
          }
        }

        // Google Calendar
        if (tool.type === 'google.calendar.event.create' || tool.type === 'google.calendar.availability.check') {
          if (tool.metadata) {
            newData.metadata = typeof tool.metadata === 'string'
              ? tool.metadata
              : JSON.stringify(tool.metadata, null, 2);
          }
          if (tool.messages) {
            newData.messages = typeof tool.messages === 'string'
              ? tool.messages
              : JSON.stringify(tool.messages, null, 2);
          }
          if (tool.parameters) {
            newData.parameters = typeof tool.parameters === 'string'
              ? tool.parameters
              : JSON.stringify(tool.parameters, null, 2);
          }
        }

        // Voicemail
        if (tool.type === 'voicemail') {
          newData.beepDetectionEnabled = tool.beepDetectionEnabled || false;
          
          if (tool.messages && Array.isArray(tool.messages) && tool.messages.length > 0) {
            const firstMessage = tool.messages[0];
            if (firstMessage.content === undefined || firstMessage.content === null) {
              // Usar mensagem do assistente
              newData.voicemailOption = 'assistant';
            } else if (firstMessage.content === '') {
              // Evitar gravar mensagens
              newData.voicemailOption = 'avoid';
            } else {
              // Script personalizado
              newData.voicemailOption = 'custom';
              newData.voicemailCustomScript = firstMessage.content || '';
            }
          } else {
            newData.voicemailOption = 'assistant';
          }
        }

        return newData;
      });
    }
  }, [tool]);

  // Normalizar headers/body (podem vir como objeto ou string do backend)
  const getHeadersFromTool = (t) => {
    const raw = t?.headers;
    if (raw == null) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return typeof raw === 'object' ? raw : {};
  };
  const getBodyFromTool = (t) => {
    const raw = t?.body;
    if (raw == null) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return typeof raw === 'object' ? raw : {};
  };

  // Sincronizar builder visual (cabeçalhos e corpo) ao editar ferramenta apiRequest
  useEffect(() => {
    if (!tool || tool.type !== 'apiRequest') return;
    const headers = getHeadersFromTool(tool);
    const body = getBodyFromTool(tool);
    setHeadersList(headersSchemaToList(headers));
    setBodyProperties(bodySchemaToProperties(body));
  }, [tool]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    try {
      let finalData = { ...formData };

      if (clienteId) {
        finalData.clienteId = clienteId;
      }

      if (formData.type === 'apiRequest') {
        // ✅ 1) NORMALIZAÇÃO E VALIDAÇÃO DE URL
        if (!formData.url) {
          toast.error("A URL é obrigatória para ferramentas do tipo API Request.");
          return;
        }

        let rawUrl = formData.url.trim();

        try {
          if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://')) {
            rawUrl = `https://${rawUrl}`;
          }
          new URL(rawUrl);
          finalData.url = rawUrl;
        } catch (error) {
          toast.error("A URL fornecida não é válida.");
          return;
        }

        // ✅ 2) MÉTODO HTTP
        const validMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
        if (!validMethods.includes(formData.method?.toUpperCase())) {
          toast.error("Método HTTP inválido.");
          return;
        }
        finalData.method = formData.method.toUpperCase();

        // ✅ 3) BODY SCHEMA – gerado pelo builder visual (bodyProperties)
        try {
          finalData.body = bodyPropertiesToSchema(bodyProperties);
          if (!finalData.body.properties || Object.keys(finalData.body.properties).length === 0) {
            finalData.body = { type: "object", properties: {}, required: [] };
          }
        } catch (error) {
          console.error("Erro ao gerar schema do corpo:", error);
          finalData.body = { type: "object", properties: {}, required: [] };
        }

        delete finalData.bodySchema;

        // ✅ 4) HEADERS – gerados pelo builder visual (headersList)
        const normalizedHeaders = headersListToSchema(headersList);
        const headerKeys = Object.keys(normalizedHeaders.properties || {});
        if (headerKeys.length > 0) {
          finalData.headers = normalizedHeaders;
        } else {
          delete finalData.headers;
        }

        // ✅ 5) CAMPOS QUE NÃO PODEM IR EM apiRequest
        delete finalData.metadata;
        delete finalData.messages;
        delete finalData.parameters;
        delete finalData.function;
      }

      // TransferCall (mantido caso você use esse tipo vindo da Vapi / Mongo)
      if (formData.type === 'transferCall') {
        finalData = {
          ...formData,
          destinations: [{
            type: "number",
            number: formData.destinationNumber,
            message: formData.transferMessage || "",
            numberE164CheckEnabled: formData.numberE164CheckEnabled !== false,
            extension: "",
            callerId: "",
            transferPlan: {
              mode: "warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary",
              message: formData.transferMessage || "",
              timeout: parseInt(formData.transferTimeout) || 60,
              sipVerb: "dial",
              holdAudioUrl: formData.holdAudioUrl || "",
              transferCompleteAudioUrl: "",
              twiml: "",
              summaryPlan: {
                messages: [],
                enabled: false,
                timeoutSeconds: 0
              },
              sipHeadersInReferToEnabled: false,
              fallbackPlan: { message: "" }
            },
            description: formData.description || ""
          }]
        };

        delete finalData.destinationNumber;
        delete finalData.transferMessage;
        delete finalData.transferTimeout;
        delete finalData.holdAudioUrl;
        delete finalData.numberE164CheckEnabled;
      }

      // Google Calendar
      if (formData.type === 'google.calendar.event.create' || formData.type === 'google.calendar.availability.check') {
        finalData = {
          ...formData,
          function: {
            name: formData.name,
            description: formData.description,
            parameters: typeof formData.parameters === 'string'
              ? JSON.parse(formData.parameters)
              : formData.parameters,
          },
          messages: typeof formData.messages === 'string'
            ? JSON.parse(formData.messages)
            : formData.messages,
          metadata: typeof formData.metadata === 'string'
            ? JSON.parse(formData.metadata)
            : formData.metadata,
          parameters: typeof formData.parameters === 'string'
            ? JSON.parse(formData.parameters)
            : formData.parameters,
        };
      }

      // Voicemail
      if (formData.type === 'voicemail') {
        let messages = [];
        
        if (formData.voicemailOption === 'assistant') {
          // Usar mensagem do assistente - messages sem content
          messages = [{
            type: "request-start",
            blocking: false
          }];
        } else if (formData.voicemailOption === 'custom') {
          // Script personalizado - messages com content preenchido
          messages = [{
            type: "request-start",
            content: formData.voicemailCustomScript || "",
            blocking: false
          }];
        } else if (formData.voicemailOption === 'avoid') {
          // Evitar gravar mensagens - messages com content vazio
          messages = [{
            type: "request-start",
            content: "",
            blocking: false
          }];
        }

        finalData = {
          ...formData,
          type: 'voicemail',
          function: {
            name: 'voicemail_tool',
            description: 'Leave a voicemail message when you determine the call reached voicemail.',
            parameters: {
              type: 'object',
              properties: {},
              required: []
            }
          },
          messages: messages,
          beepDetectionEnabled: formData.beepDetectionEnabled || false
        };

        // Remover campos que não devem ser enviados
        delete finalData.voicemailOption;
        delete finalData.voicemailCustomScript;
        delete finalData.url;
        delete finalData.method;
        delete finalData.headers;
        delete finalData.body;
        delete finalData.metadata;
        delete finalData.parameters;
        delete finalData.bodySchema;
        delete finalData.showHeaders;
      }

      if (!finalData.name) {
        if (finalData.type === 'voicemail') {
          finalData.name = null;
        } else {
          finalData.name = finalData.type === 'transferCall' ? 'Transferir Chamada' : 'Nova Ferramenta';
        }
      }

      if (!finalData.description) {
        finalData.description = finalData.type === 'voicemail' ? null : '';
      }

      if (clienteId && typeof clienteId !== 'undefined') {
        finalData.clienteId = clienteId;
      }

      if (formData.orgId) {
        finalData.orgId = formData.orgId;
      }

      onSave(finalData);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao processar dados da ferramenta.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-4">
      <h2 className="text-2xl font-bold mb-6 text-white">{tool ? 'Editar Ferramenta' : 'Criar Nova Ferramenta'}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className={labelStyle}>Nome da Ferramenta</label>
          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            className={inputStyle}
            required={formData.type !== 'voicemail'}
            placeholder="Ex: Transferir para Suporte"
          />
        </div>
        <div>
          <label htmlFor="type" className={labelStyle}>Tipo</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            className={selectStyle}
          >
            <option value="apiRequest">API Request</option>
            <option value="google.calendar.event.create">Google Calendar Event Create</option>
            <option value="google.calendar.availability.check">Google Calendar Availability Check</option>
            <option value="voicemail">Voicemail</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="description" className={labelStyle}>Descrição</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className={inputStyle}
          rows="2"
          placeholder="Descreva o propósito desta ferramenta"
        />
      </div>

      <div>
        <label htmlFor="orgId" className={labelStyle}>ID da Organização</label>
        <input
          id="orgId"
          name="orgId"
          type="text"
          value={formData.orgId}
          onChange={handleChange}
          className={inputStyle}
          placeholder="Ex: 61d09b13-129f-4353-9eb3-0e7ee89eb6b8"
        />
      </div>

      {formData.type === 'apiRequest' && (
        <>
          <CollapsibleSection
            title="Configuração base"
            description="Configure o nome da ferramenta, a URL e o método HTTP."
            icon={Cog6ToothIcon}
            open={apiRequestSections.base}
            onToggle={() => setApiRequestSections((s) => ({ ...s, base: !s.base }))}
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="url" className={labelStyle}>URL da requisição *</label>
                <input
                  id="url"
                  name="url"
                  type="url"
                  value={formData.url}
                  onChange={handleChange}
                  className={inputStyle}
                  required
                  placeholder="ex: https://api.exemplo.com/endpoint"
                />
                <p className="mt-1 text-xs text-muted-foreground">A URL do endpoint para onde a requisição será enviada. Deve usar HTTPS.</p>
              </div>
              <div>
                <label htmlFor="method" className={labelStyle}>Método HTTP da requisição *</label>
                <select
                  id="method"
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  className={selectStyle}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                  <option value="HEAD">HEAD</option>
                  <option value="OPTIONS">OPTIONS</option>
                </select>
                <p className="mt-1 text-xs text-muted-foreground">O método HTTP a ser usado na requisição.</p>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Autorização"
            description="Configure a autenticação para as requisições da API."
            icon={LockClosedIcon}
            open={apiRequestSections.auth}
            onToggle={() => setApiRequestSections((s) => ({ ...s, auth: !s.auth }))}
          >
            <p className="text-sm text-muted-foreground">Use a seção &quot;Cabeçalhos da requisição&quot; para enviar chaves de API (ex.: Authorization, X-API-Key).</p>
          </CollapsibleSection>

          <CollapsibleSection
            title="Cabeçalhos da requisição"
            description="Cabeçalhos personalizados a serem incluídos na requisição."
            icon={Bars3Icon}
            open={apiRequestSections.headers}
            onToggle={() => setApiRequestSections((s) => ({ ...s, headers: !s.headers }))}
            badgeCount={headersList.filter((h) => String(h.key || '').trim()).length}
          >
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setHeadersList((prev) => [...prev, { key: '', value: '' }])}
                >
                  <PlusCircleIcon className="h-4 w-4 mr-1" />
                  Adicionar cabeçalho
                </Button>
              </div>
              {headersList.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
                  Nenhum cabeçalho definido. Clique em &quot;Adicionar cabeçalho&quot; para incluir.
                </p>
              ) : (
                <ul className="space-y-2">
                  {headersList.map((h, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={h.key}
                        onChange={(e) => setHeadersList((prev) => {
                          const next = [...prev];
                          next[i] = { ...next[i], key: e.target.value };
                          return next;
                        })}
                        className={`${inputStyle} flex-1`}
                        placeholder="Nome do cabeçalho"
                      />
                      <input
                        type="text"
                        value={h.value}
                        onChange={(e) => setHeadersList((prev) => {
                          const next = [...prev];
                          next[i] = { ...next[i], value: e.target.value };
                          return next;
                        })}
                        className={`${inputStyle} flex-1`}
                        placeholder="Valor"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setHeadersList((prev) => prev.filter((_, j) => j !== i))}
                        title="Remover cabeçalho"
                      >
                        <TrashIcon className="h-5 w-5 text-destructive" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Corpo da requisição"
            description="Defina a estrutura do corpo da requisição usando o construtor de esquema."
            icon={Bars3Icon}
            open={apiRequestSections.body}
            onToggle={() => setApiRequestSections((s) => ({ ...s, body: !s.body }))}
            badgeCount={bodyProperties.filter((p) => p && String(p.name || '').trim()).length}
          >
            <div className="space-y-3">
              {editingPropertyIndex !== null && editingProperty && (
                <div className={`rounded-lg border bg-muted/30 p-4 space-y-4 ${editingProperty.type === 'object' ? 'border-teal-500/50' : 'border-border'}`}>
                  <h4 className="font-medium text-foreground">
                    {editingPropertyIndex >= 0 ? 'Editar propriedade' : 'Nova propriedade'}
                  </h4>
                  <div>
                    <Label className={labelStyle}>Nome</Label>
                    <input
                      type="text"
                      value={editingProperty.name}
                      onChange={(e) => setEditingProperty((p) => ({ ...p, name: e.target.value }))}
                      className={inputStyle}
                      placeholder="ex: query, dados, filtro"
                    />
                  </div>
                  <div>
                    <Label className={labelStyle}>Tipo</Label>
                    <select
                      value={editingProperty.type}
                      onChange={(e) => setEditingProperty((p) => ({ ...p, type: e.target.value }))}
                      className={selectStyle}
                    >
                      <option value="string">string</option>
                      <option value="number">number</option>
                      <option value="integer">integer</option>
                      <option value="boolean">boolean</option>
                      <option value="object">object</option>
                      <option value="array">array</option>
                    </select>
                  </div>
                  {editingProperty.type !== 'object' && (
                  <div>
                    <Label className={labelStyle}>Valor padrão</Label>
                    <input
                      type="text"
                      value={editingProperty.defaultValue}
                      onChange={(e) => setEditingProperty((p) => ({ ...p, defaultValue: e.target.value }))}
                      className={inputStyle}
                      placeholder="Valor padrão ou fixo desta propriedade"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Valor padrão ou fixo para esta propriedade.</p>
                  </div>
                  )}
                  <div>
                    <Label className={labelStyle}>Descrição</Label>
                    {editingProperty.type === 'object' ? (
                      <textarea
                        value={editingProperty.description}
                        onChange={(e) => setEditingProperty((p) => ({ ...p, description: e.target.value }))}
                        className={inputStyle}
                        rows={3}
                        placeholder="Descrição da propriedade"
                      />
                    ) : (
                      <input
                        type="text"
                        value={editingProperty.description}
                        onChange={(e) => setEditingProperty((p) => ({ ...p, description: e.target.value }))}
                        className={inputStyle}
                        placeholder="Descrição da propriedade (opcional)"
                      />
                    )}
                  </div>
                  {editingProperty.type === 'string' && (
                    <div>
                      <Label className={labelStyle}>Valores enum</Label>
                      <p className="text-xs text-muted-foreground mb-1">
                        {editingProperty.enumValues && editingProperty.enumValues.length > 0
                          ? 'Valores definidos'
                          : 'Nenhum valor enum definido'}
                      </p>
                      <div className="flex flex-wrap gap-2 items-center">
                        {(editingProperty.enumValues || []).map((v, ei) => (
                          <span key={ei} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-sm">
                            {v}
                            <button
                              type="button"
                              onClick={() => setEditingProperty((p) => ({
                                ...p,
                                enumValues: (p.enumValues || []).filter((_, j) => j !== ei),
                              }))}
                              className="text-destructive hover:underline"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingProperty((p) => ({
                            ...p,
                            enumValues: [...(p.enumValues || []), ''],
                          }))}
                        >
                          + Adicionar valor
                        </Button>
                      </div>
                      {(editingProperty.enumValues || []).some((v, i) => v === '') && (
                        <input
                          type="text"
                          className={`${inputStyle} mt-2`}
                          placeholder="Digite o valor e confirme"
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (!v) return;
                            setEditingProperty((p) => ({
                              ...p,
                              enumValues: (p.enumValues || []).map((x, i) => (x === '' && i === p.enumValues.length - 1 ? v : x)).filter(Boolean),
                            }));
                          }}
                        />
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="prop-required"
                      checked={editingProperty.required === true}
                      onChange={(e) => setEditingProperty((p) => ({ ...p, required: e.target.checked }))}
                      className="rounded border-input text-primary"
                    />
                    <Label htmlFor="prop-required">Obrigatório</Label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={() => { setEditingPropertyIndex(null); setEditingPropertyPath([]); setEditingProperty(null); }}>
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      className={editingProperty.type === 'object' ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}
                      onClick={() => {
                        if (!editingProperty.name || !String(editingProperty.name).trim()) {
                          toast.error('Informe o nome da propriedade.');
                          return;
                        }
                        const prop = {
                          ...editingProperty,
                          name: String(editingProperty.name).trim(),
                          enumValues: (editingProperty.enumValues || []).map((v) => String(v).trim()).filter(Boolean),
                        };
                        if (editingProperty.type === 'object' && !Array.isArray(prop.children)) {
                          prop.children = [];
                        }
                        const path = editingPropertyPath;
                        const arr = getPropertiesAtPath(bodyProperties, path);
                        const newArr = editingPropertyIndex >= 0
                          ? arr.map((item, j) => (j === editingPropertyIndex ? prop : item))
                          : [...arr, prop];
                        setBodyProperties(path.length === 0 ? newArr : setPropertiesAtPath(bodyProperties, path, newArr));
                        setEditingPropertyIndex(null);
                        setEditingPropertyPath([]);
                        setEditingProperty(null);
                      }}
                    >
                      Aplicar
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingPropertyPath([]);
                    setEditingPropertyIndex(-1);
                    setEditingProperty({ name: '', type: 'string', defaultValue: '', description: '', enumValues: [], required: false });
                  }}
                >
                  <PlusCircleIcon className="h-4 w-4 mr-1" />
                  Adicionar propriedade
                </Button>
              </div>
              {bodyProperties.length === 0 && editingPropertyIndex === null ? (
                <div className="py-8 text-center border border-dashed border-border rounded-lg">
                  <p className="text-sm text-muted-foreground">Nenhum parâmetro definido.</p>
                  <p className="text-xs text-muted-foreground mt-1">Clique em &quot;Adicionar propriedade&quot; para definir os parâmetros da ferramenta.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {bodyProperties.map((p, i) => (
                    <BodyPropertyItem
                      key={i}
                      prop={p}
                      index={i}
                      path={[i]}
                      pathKey={String(i)}
                      bodyProperties={bodyProperties}
                      setBodyProperties={setBodyProperties}
                      expandedBodyPaths={expandedBodyPaths}
                      setExpandedBodyPaths={setExpandedBodyPaths}
                      setEditingPropertyIndex={setEditingPropertyIndex}
                      setEditingPropertyPath={setEditingPropertyPath}
                      setEditingProperty={setEditingProperty}
                      getPropertiesAtPath={getPropertiesAtPath}
                      setPropertiesAtPath={setPropertiesAtPath}
                      labelStyle={labelStyle}
                      inputStyle={inputStyle}
                    />
                  ))}
                </ul>
              )}
            </div>
          </CollapsibleSection>
        </>
      )}

      {formData.type === 'transferCall' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="destinationNumber" className={labelStyle}>Número de Destino</label>
              <input
                id="destinationNumber"
                name="destinationNumber"
                type="text"
                value={formData.destinationNumber || ''}
                onChange={handleChange}
                className={inputStyle}
                placeholder="Ex: +5511987654321"
                required
              />
              <p className="mt-1 text-xs text-gray-400">
                Formato internacional com código do país (ex: +55 para Brasil)
              </p>
            </div>
            <div>
              <label htmlFor="transferTimeout" className={labelStyle}>Timeout (segundos)</label>
              <input
                id="transferTimeout"
                name="transferTimeout"
                type="number"
                value={formData.transferTimeout || '60'}
                onChange={handleChange}
                className={inputStyle}
                min="10"
                max="300"
              />
            </div>
          </div>

          <div>
            <label htmlFor="transferMessage" className={labelStyle}>Mensagem de Transferência</label>
            <textarea
              id="transferMessage"
              name="transferMessage"
              value={formData.transferMessage || ''}
              onChange={handleChange}
              className={inputStyle}
              rows="2"
              placeholder="Mensagem a ser reproduzida antes da transferência"
            />
          </div>

          <div>
            <label htmlFor="holdAudioUrl" className={labelStyle}>URL do Áudio de Espera</label>
            <input
              id="holdAudioUrl"
              name="holdAudioUrl"
              type="url"
              value={formData.holdAudioUrl || ''}
              onChange={handleChange}
              className={inputStyle}
              placeholder="URL para áudio durante a espera"
            />
          </div>

          <div className="mt-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="numberE164CheckEnabled"
                checked={formData.numberE164CheckEnabled !== false}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, numberE164CheckEnabled: e.target.checked }));
                }}
                className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4 mr-2"
              />
              <span className="text-gray-300">Verificar formato E.164 para o número</span>
            </label>
            <p className="mt-1 text-xs text-gray-400 ml-6">
              Recomendado para garantir que o número está no formato correto
            </p>
          </div>
        </>
      )}

      {(formData.type === 'google.calendar.event.create' || formData.type === 'google.calendar.availability.check') && (
        <>
          <div>
            <label htmlFor="metadata" className={labelStyle}>Metadata (JSON)</label>
            <textarea
              id="metadata"
              name="metadata"
              value={formData.metadata}
              onChange={handleChange}
              className={`${inputStyle} font-mono`}
              rows="4"
            />
          </div>

          <div>
            <label htmlFor="messages" className={labelStyle}>Messages (JSON)</label>
            <textarea
              id="messages"
              name="messages"
              value={formData.messages}
              onChange={handleChange}
              className={`${inputStyle} font-mono`}
              rows="4"
            />
          </div>

          <div>
            <label htmlFor="parameters" className={labelStyle}>Parameters (JSON)</label>
            <textarea
              id="parameters"
              name="parameters"
              value={formData.parameters}
              onChange={handleChange}
              className={`${inputStyle} font-mono`}
              rows="4"
            />
          </div>
        </>
      )}

      {formData.type === 'voicemail' && (
        <>
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
            <h3 className="text-lg font-semibold text-white mb-2">Roteiro para mensagem de voz</h3>
            <p className="text-sm text-gray-400 mb-4">
              Quando o sistema de gerenciamento de chamadas (LLM) determinar que chegou à caixa postal, ele ligará para esta ferramenta e reproduzirá o roteiro abaixo antes que o atendente encerre a chamada.
            </p>
            
            <p className="text-sm font-medium text-gray-300 mb-4">O que a pessoa que liga deve ouvir?</p>

            <div className="space-y-4">
              {/* Opção 1: Usar mensagem do assistente */}
              <label className="flex items-start p-4 bg-gray-600 rounded-lg border-2 cursor-pointer transition-colors hover:bg-gray-500"
                style={{ borderColor: formData.voicemailOption === 'assistant' ? '#a855f7' : 'transparent' }}>
                <input
                  type="radio"
                  name="voicemailOption"
                  value="assistant"
                  checked={formData.voicemailOption === 'assistant'}
                  onChange={handleChange}
                  className="mt-1 mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-white mb-1">
                    Use a mensagem de correio de voz do assistente.
                  </div>
                  <div className="text-xs text-gray-400">
                    Recorre à configuração 'voicemailMessage' do assistente.
                  </div>
                </div>
              </label>

              {/* Opção 2: Script personalizado */}
              <label className="flex items-start p-4 bg-gray-600 rounded-lg border-2 cursor-pointer transition-colors hover:bg-gray-500"
                style={{ borderColor: formData.voicemailOption === 'custom' ? '#a855f7' : 'transparent' }}>
                <input
                  type="radio"
                  name="voicemailOption"
                  value="custom"
                  checked={formData.voicemailOption === 'custom'}
                  onChange={handleChange}
                  className="mt-1 mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-white mb-1">
                    Usar script personalizado
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    Forneça uma mensagem para o assistente falar assim que ele confirmar a saudação da caixa postal.
                  </div>
                  {formData.voicemailOption === 'custom' && (
                    <textarea
                      name="voicemailCustomScript"
                      value={formData.voicemailCustomScript}
                      onChange={handleChange}
                      className={`${inputStyle} font-mono text-sm`}
                      rows="3"
                      placeholder="Ex: Hi, this is {{company}}. {{message}}. Please call us back at {{phone}}."
                    />
                  )}
                </div>
              </label>

              {/* Opção 3: Evitar gravar mensagens */}
              <label className="flex items-start p-4 bg-gray-600 rounded-lg border-2 cursor-pointer transition-colors hover:bg-gray-500"
                style={{ borderColor: formData.voicemailOption === 'avoid' ? '#a855f7' : 'transparent' }}>
                <input
                  type="radio"
                  name="voicemailOption"
                  value="avoid"
                  checked={formData.voicemailOption === 'avoid'}
                  onChange={handleChange}
                  className="mt-1 mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-white mb-1">
                    Evite gravar mensagens de voz.
                  </div>
                  <div className="text-xs text-gray-400">
                    O assistente encerrará a chamada silenciosamente após acionar a ferramenta.
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="mt-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="beepDetectionEnabled"
                checked={formData.beepDetectionEnabled}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, beepDetectionEnabled: e.target.checked }));
                }}
                className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4 mr-2"
              />
              <span className="text-gray-300">Habilitar detecção de beep</span>
            </label>
            <p className="mt-1 text-xs text-gray-400 ml-6">
              Detecta o beep da caixa postal antes de gravar a mensagem
            </p>
          </div>
        </>
      )}

      <div className="pt-4 flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Salvando...' : 'Salvar Ferramenta'}
        </button>
      </div>
    </form>
  );
};

// Principal
export default function Tools() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [loadingEditTool, setLoadingEditTool] = useState(false);
  const [isAssociarModalOpen, setIsAssociarModalOpen] = useState(false);
  const [ferramentaParaAssociar, setFerramentaParaAssociar] = useState(null);
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState([]);
  const [usuariosDaFerramenta, setUsuariosDaFerramenta] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [nomeFiltro, setNomeFiltro] = useState('');

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const isAdmin = userData.role === 'admin';
  const isSuperAdmin = userData.role === 'super_admin';
  const canUseClienteFilter = isAdmin || isSuperAdmin;

  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      return null;
    }
    return { headers: { Authorization: `Bearer ${token}` } };
  };
  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') {
      if (value._id) return String(value._id);
      if (value.id) return String(value.id);
    }
    return String(value);
  };
  const clientesMap = useMemo(() => (
    new Map(clientes.map(c => [String(c._id), c]))
  ), [clientes]);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      const tokenClient = localStorage.getItem('tokenClient');
      const params = {};
      if (clienteFiltro) {
        params.clienteId = clienteFiltro;
      } else if (!isAdmin && !isSuperAdmin && clienteId) {
        params.clienteId = clienteId;
      }
      if (tokenClient) params.tokenClient = tokenClient;
      const config = { ...getAuthConfig(), params };

      const response = await axios.get(`${API_BASE_URL}/api/tools`, config);
      setTools(response.data);
      setError('');
    } catch (err) {
      setError('Erro ao listar as ferramentas.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clienteId, isAdmin, isSuperAdmin, clienteFiltro]);

  const fetchClientes = useCallback(async () => {
    if (!canUseClienteFilter) return;
    const config = getAuthConfig();
    if (!config) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/usuarios`, config);
      setClientes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  }, [canUseClienteFilter]);

  const fetchUsuariosDisponiveis = async () => {
    try {
      const config = getAuthConfig();
      if (!config) return;
      const response = await axios.get(`${API_BASE_URL}/api/usuarios`, config);
      setUsuariosDisponiveis(response.data);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      toast.error("Falha ao carregar lista de usuários");
    }
  };

  const fetchUsuariosDaFerramenta = async (ferramentaId) => {
    try {
      const config = getAuthConfig();
      if (!config) return;
      const response = await axios.get(`${API_BASE_URL}/api/tools/${ferramentaId}/users`, config);
      setUsuariosDaFerramenta(response.data);
    } catch (error) {
      console.error("Erro ao buscar usuários da ferramenta:", error);
      toast.error("Falha ao carregar usuários associados");
    }
  };

  const handleOpenAssociarModal = (ferramenta) => {
    if (!isAdmin) {
      toast.error("Apenas administradores podem associar usuários às ferramentas.");
      return;
    }
    setFerramentaParaAssociar(ferramenta);
    fetchUsuariosDisponiveis();
    fetchUsuariosDaFerramenta(ferramenta.id || ferramenta._id);
    setIsAssociarModalOpen(true);
  };

  const handleSaveAssociacoes = async (userTokensMap) => {
    if (!isAdmin) {
      toast.error("Apenas administradores podem modificar associações de usuários.");
      return;
    }
    if (!ferramentaParaAssociar) return;

    const toastId = toast.loading("Salvando associações...");
    try {
      const config = getAuthConfig();
      if (!config) return;
      const payload = {
        userTokens: userTokensMap,
        tokenClient: localStorage.getItem('tokenClient')
      };
      await axios.post(
        `${API_BASE_URL}/api/tools/${ferramentaParaAssociar.id || ferramentaParaAssociar._id}/associate-users`,
        payload,
        config
      );
      toast.success("Associações atualizadas com sucesso!", { id: toastId });
      setIsAssociarModalOpen(false);
      fetchTools();
    } catch (error) {
      console.error("Erro ao salvar associações:", error);
      toast.error("Falha ao salvar associações", { id: toastId });
    }
  };

  const handleSaveTool = async (formData) => {
    setIsSubmitting(true);
    const isEditing = !!formData.id;
    const url = isEditing ? `${API_BASE_URL}/api/tools/${formData.id}` : `${API_BASE_URL}/api/tools`;
    const method = isEditing ? 'put' : 'post';
    const toastId = toast.loading(isEditing ? 'Atualizando ferramenta...' : 'Criando ferramenta...');

    try {
      const payload = {
        ...formData,
        ...(clienteId && { clienteId }),
        tokenClient: localStorage.getItem('tokenClient')
      };

      await axios[method](url, payload, getAuthConfig());
      toast.success(isEditing ? 'Ferramenta atualizada!' : 'Ferramenta criada!', { id: toastId });
      setIsModalOpen(false);
      fetchTools();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ocorreu um erro.', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTool = async (tool) => {
    const id = tool.vapiId || tool.id || tool._id;
    if (!id) {
      toast.error('Ferramenta sem ID.');
      return;
    }
    setLoadingEditTool(true);
    try {
      const config = getAuthConfig();
      if (!config) return;
      const response = await axios.get(`${API_BASE_URL}/api/tools/${id}`, config);
      setEditingTool(response.data);
      setIsModalOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao carregar ferramenta para edição.');
      console.error(err);
    } finally {
      setLoadingEditTool(false);
    }
  };

  const handleDeleteTool = async (tool) => {
    if (window.confirm(`Tem certeza que deseja remover a ferramenta "${asText(tool.name || tool.function?.name)}"?`)) {
      const toastId = toast.loading('Excluindo...');
      try {
        const config = getAuthConfig();
        const payload = { tokenClient: localStorage.getItem('tokenClient') };
        await axios.delete(`${API_BASE_URL}/api/tools/${tool.id || tool._id}`, { ...config, data: payload });
        toast.success('Ferramenta removida!', { id: toastId });
        fetchTools();
      } catch (err) {
        toast.error('Erro ao remover.', { id: toastId });
      }
    }
  };

  useEffect(() => { fetchTools(); }, [fetchTools]);
  useEffect(() => { fetchClientes(); }, [fetchClientes]);

  const toolsFiltradas = useMemo(() => {
    let list = tools;
    if (canUseClienteFilter && clienteFiltro) {
      list = list.filter(tool => normalizeId(tool.clienteId) === clienteFiltro);
    }
    const termo = (nomeFiltro || '').trim().toLowerCase();
    if (termo) {
      list = list.filter(tool => {
        const nome = asText(tool.name ?? tool.function?.name ?? '').toLowerCase();
        const desc = asText(tool.description ?? tool.function?.description ?? '').toLowerCase();
        return nome.includes(termo) || desc.includes(termo);
      });
    }
    return list;
  }, [tools, canUseClienteFilter, clienteFiltro, nomeFiltro]);

  return (
    <>
      {/* Modal criação/edição */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ToolForm
          key={editingTool ? (editingTool.vapiId || editingTool.id || editingTool._id || 'edit') : 'new'}
          tool={editingTool}
          onSave={handleSaveTool}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          clienteId={clienteId}
        />
      </Modal>

      {/* Modal detalhes */}
      <Modal isOpen={!!selectedTool} onClose={() => setSelectedTool(null)}>
        {selectedTool && (
          <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-4">
            <h2 className="text-2xl font-bold mb-6 text-white">
              Detalhes da Ferramenta: {asText(selectedTool.name ?? selectedTool.function?.name)}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-gray-300">Nome:</label>
                <p className="text-gray-200 font-medium">
                  {asText(selectedTool.name ?? selectedTool.function?.name)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300">Tipo:</label>
                <p className="text-gray-200 font-medium">{asText(selectedTool.type)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300">ID:</label>
                <p className="text-gray-200 font-medium">{asText(selectedTool.vapiId)}</p>
              </div>
              {selectedTool.createdAt && (
                <div>
                  <label className="text-sm font-medium text-gray-300">Criado em:</label>
                  <p className="text-gray-200 font-medium">
                    {new Date(selectedTool.createdAt).toLocaleString()}
                  </p>
                </div>
              )}
              {selectedTool.updatedAt && (
                <div>
                  <label className="text-sm font-medium text-gray-300">Atualizado em:</label>
                  <p className="text-gray-200 font-medium">
                    {new Date(selectedTool.updatedAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {(selectedTool.description || selectedTool.function?.description) && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-300">Descrição:</label>
                <div className="text-gray-200 bg-gray-700 p-3 rounded mt-1 whitespace-pre-wrap">
                  {asText(selectedTool.description ?? selectedTool.function?.description)}
                </div>
              </div>
            )}

            {selectedTool.type === 'transferCall' && selectedTool.destinations && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-300">Configurações de Transferência:</h3>
                <div className="mt-2 space-y-4">
                  {selectedTool.destinations.map((dest, index) => (
                    <div key={index} className="p-4 bg-gray-700 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-300">Número de Destino:</p>
                          <p className="text-gray-200">{asText(dest.number)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-300">Timeout:</p>
                          <p className="text-gray-200">
                            {asText(dest.transferPlan?.timeout || '60')} segundos
                          </p>
                        </div>
                      </div>
                      {dest.message && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-300">Mensagem:</p>
                          <p className="text-gray-200">{asText(dest.message)}</p>
                        </div>
                      )}
                      {dest.transferPlan?.holdAudioUrl && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-300">Áudio de Espera:</p>
                          <a
                            href={dest.transferPlan.holdAudioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-400 hover:text-purple-300 break-all"
                          >
                            {asText(dest.transferPlan.holdAudioUrl)}
                          </a>
                        </div>
                      )}
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-300">Verificação E.164:</p>
                        <p className="text-gray-200">
                          {dest.numberE164CheckEnabled ? 'Ativada' : 'Desativada'}
                        </p>
                      </div>
                      {dest.description && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-300">Descrição:</p>
                          <p className="text-gray-200">{asText(dest.description)}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedTool.type === 'apiRequest' && (
              <>
                {selectedTool.method && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-300">Método HTTP:</label>
                    <p className="text-gray-200 font-medium">{asText(selectedTool.method)}</p>
                  </div>
                )}
                {selectedTool.url && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-300">URL:</label>
                    <p className="text-gray-200 font-medium break-all">
                      {asText(selectedTool.url)}
                    </p>
                  </div>
                )}
                {selectedTool.headers && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-300">Headers:</label>
                    <pre className="text-gray-200 bg-gray-700 p-3 rounded mt-1 whitespace-pre-wrap">
                      {JSON.stringify(selectedTool.headers, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}

            {(selectedTool.type === 'google.calendar.event.create' || selectedTool.type === 'google.calendar.availability.check') && (
              <>
                {selectedTool.metadata && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-300">Metadata:</label>
                    <pre className="text-gray-200 bg-gray-700 p-3 rounded mt-1 whitespace-pre-wrap">
                      {JSON.stringify(selectedTool.metadata, null, 2)}
                    </pre>
                  </div>
                )}
                {selectedTool.messages && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-300">Messages:</label>
                    <pre className="text-gray-200 bg-gray-700 p-3 rounded mt-1 whitespace-pre-wrap">
                      {JSON.stringify(selectedTool.messages, null, 2)}
                    </pre>
                  </div>
                )}
                {selectedTool.parameters && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-300">Parameters:</label>
                    <pre className="text-gray-200 bg-gray-700 p-3 rounded mt-1 whitespace-pre-wrap">
                      {JSON.stringify(selectedTool.parameters, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}

            {isAdmin && selectedTool.usuarios && selectedTool.usuarios.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-300">Usuários Associados:</h3>
                <div className="text-gray-200 bg-gray-700 p-3 rounded mt-1">
                  <ul className="list-disc list-inside pl-2">
                    {selectedTool.usuarios.map(userId => {
                      const user = usuariosDisponiveis.find(
                        u => (u._id && String(u._id) === String(userId))
                      );
                      return (
                        <li key={String(userId)}>
                          {user
                            ? `${user.nome || user.name || user.username} (${user.email})`
                            : asText(userId)}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedTool(null)}
                className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de associação */}
      {isAssociarModalOpen && (
        <AssociateUsersModal
          isOpen={isAssociarModalOpen}
          onClose={() => setIsAssociarModalOpen(false)}
          tool={ferramentaParaAssociar}
          users={usuariosDisponiveis}
          toolUsers={usuariosDaFerramenta}
          onSave={handleSaveAssociacoes}
          isAdmin={isAdmin}
        />
      )}

      {/* Conteúdo principal */}
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Gerenciador de Ferramentas</h1>
            <p className="text-sm text-muted-foreground">Crie e gerencie integrações e automações.</p>
          </div>
          <Button
            onClick={() => {
              setSelectedTool(null);
              setEditingTool(null);
              setIsModalOpen(true);
            }}
          >
            <PlusIcon className="h-5 w-5" />
            Criar Ferramenta
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[200px] flex-1 max-w-md">
            <Label className="text-xs text-muted-foreground">Buscar por nome</Label>
            <input
              type="text"
              value={nomeFiltro}
              onChange={(e) => setNomeFiltro(e.target.value)}
              placeholder="Nome ou descrição da ferramenta..."
              className={`${inputStyle} mt-1`}
            />
          </div>
          {canUseClienteFilter && (
            <div className="max-w-md">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <select
                value={clienteFiltro}
                onChange={(e) => setClienteFiltro(e.target.value)}
                className={`${selectStyle} mt-1`}
              >
                <option value="">Todos os clientes</option>
                {clientes.map(cliente => (
                  <option key={cliente._id} value={String(cliente._id)}>
                    {cliente.nome || cliente.name || cliente.email || String(cliente._id)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="p-4 font-semibold">Nome</th>
                <th className="p-4 font-semibold">Descrição</th>
                {canUseClienteFilter && <th className="p-4 font-semibold">Cliente</th>}
                <th className="p-4 font-semibold text-center">Tipo</th>
                <th className="p-4 font-semibold text-center">Método</th>
                <th className="p-4 font-semibold text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canUseClienteFilter ? 6 : 5} className="text-center p-8 text-muted-foreground">Carregando ferramentas...</td></tr>
              ) : error ? (
                <tr><td colSpan={canUseClienteFilter ? 6 : 5} className="text-center p-8 text-destructive">{error}</td></tr>
              ) : toolsFiltradas.length === 0 ? (
                <tr><td colSpan={canUseClienteFilter ? 6 : 5} className="text-center p-8 text-muted-foreground">Nenhuma ferramenta criada.</td></tr>
              ) : (
                toolsFiltradas.map((tool) => (
                  <tr
                    key={String(tool.id || tool._id)}
                    className="border-b border-border hover:bg-muted/40 cursor-pointer"
                    onClick={() => setSelectedTool(tool)}
                  >
                    <td className="p-4">
                      <div className="flex items-center">
                        <BeakerIcon className="h-5 w-5 text-primary mr-2" />
                        {asText(tool.name ?? tool.function?.name)}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {tool.description || tool.function?.description
                        ? shortText(tool.description ?? tool.function?.description, 30)
                        : 'Sem descrição'}
                    </td>
                    {canUseClienteFilter && (
                      <td className="p-4">
                        {(() => {
                          const clienteIdValue = normalizeId(tool.clienteId);
                          const cliente = clientesMap.get(clienteIdValue);
                          return cliente?.nome || cliente?.name || cliente?.email || clienteIdValue || 'N/A';
                        })()}
                      </td>
                    )}
                    <td className="p-4 text-center">
                      <Badge variant="secondary">
                        {asText(tool.type === 'transferCall' ? 'Transferência' : tool.type)}
                      </Badge>
                    </td>
                    <td className="p-4 text-center">
                      {tool.method ? (
                        <Badge variant="outline">{asText(tool.method)}</Badge>
                      ) : (
                        <Badge variant="outline">
                          {asText(tool.type === 'transferCall' ? 'Transfer Call' : tool.type)}
                        </Badge>
                      )}
                    </td>
                    <td className="p-4 text-center flex justify-center items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTool(null);
                          handleEditTool(tool);
                        }}
                        title="Editar"
                        disabled={loadingEditTool}
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAssociarModal(tool);
                          }}
                          title="Associar Usuários"
                        >
                          <UserPlusIcon className="h-5 w-5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTool(null);
                          handleDeleteTool(tool);
                        }}
                        title="Remover"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}

// Modal de associação
const AssociateUsersModal = ({ isOpen, onClose, tool, users, toolUsers, onSave, isAdmin }) => {
  const [userTokensMap, setUserTokensMap] = useState({});
  const [loading, setLoading] = useState(true);

  const normalizeId = (id) => {
    if (!id) return '';
    if (typeof id === 'object' && id !== null) {
      if ('toString' in id) return id.toString();
      if ('_id' in id) return normalizeId(id._id);
      if ('id' in id) return normalizeId(id.id);
    }
    return String(id);
  };

  const getUserIds = (userList) => {
    if (!userList || !Array.isArray(userList)) return [];
    return userList
      .map(user => {
        if (typeof user === 'string' || (typeof user === 'object' && 'toString' in user)) {
          return normalizeId(user);
        }
        if (typeof user === 'object' && user !== null) {
          if (user._id) return normalizeId(user._id);
          if (user.id) return normalizeId(user.id);
        }
        return null;
      })
      .filter(id => id);
  };

  useEffect(() => {
    if (isOpen) {
      const userIds = getUserIds(toolUsers || []);
      const initialMap = {};
      userIds.forEach(userId => {
        const user = users.find(u => normalizeId(u._id || u.id) === userId);
        if (user && user.token) {
          initialMap[userId] = user.token;
        } else {
          initialMap[userId] = '';
        }
      });
      setUserTokensMap(initialMap);
      setLoading(false);
    }
  }, [isOpen, toolUsers, users]);

  const handleTokenChange = (userId, token) => {
    if (!isAdmin) return;
    setUserTokensMap(prev => ({
      ...prev,
      [userId]: token
    }));
  };

  const handleToggleUser = (userId) => {
    if (!isAdmin) return;
    const normalizedUserId = normalizeId(userId);
    setUserTokensMap(prev => {
      if (prev[normalizedUserId] !== undefined) {
        const newMap = { ...prev };
        delete newMap[normalizedUserId];
        return newMap;
      } else {
        return {
          ...prev,
          [normalizedUserId]: ''
        };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Apenas administradores podem salvar associações.");
      return;
    }
    onSave(userTokensMap);
  };

  if (!isOpen) return null;

  const normalizedUsers = users.map(user => ({
    ...user,
    normalizedId: normalizeId(user._id || user.id)
  }));

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="p-4 text-center text-muted-foreground">Carregando dados...</div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-2xl font-semibold">Associar Usuários à Ferramenta</h2>

        {!isAdmin && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-700 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-200">
            Você não tem permissão para modificar associações de usuários.
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Selecione os usuários que terão acesso a essa ferramenta:{' '}
          <span className="font-semibold text-foreground">{asText(tool?.name)}</span>
        </p>

        <div className="max-h-96 overflow-y-auto rounded-lg border border-border bg-muted/30 p-2">
          {users.length === 0 ? (
            <p className="p-4 text-center text-muted-foreground">Nenhum usuário disponível</p>
          ) : (
            normalizedUsers.map(user => {
              const userId = normalizeId(user._id || user.id);
              const isSelected = userTokensMap[userId] !== undefined;
              return (
                <div key={userId} className="flex items-center gap-2 border-b border-border p-2 last:border-b-0">
                  <input
                    type="checkbox"
                    id={`user-${userId}`}
                    checked={isSelected}
                    onChange={() => handleToggleUser(user._id || user.id)}
                    disabled={!isAdmin}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <label htmlFor={`user-${userId}`} className="block flex-1 text-sm text-foreground">
                    <div className="font-medium">{user.nome || user.name || user.username || 'Usuário sem nome'}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                  </label>
                  {isSelected && (
                    <div className="ml-2">
                      <input
                        type="text"
                        value={userTokensMap[userId] || ''}
                        onChange={(e) => handleTokenChange(userId, e.target.value)}
                        className="h-8 w-40 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                        placeholder="Token do cliente"
                        disabled={!isAdmin}
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="pt-4 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!isAdmin}>
            Salvar Associações
          </Button>
        </div>
      </form>
    </Modal>
  );
};
